import React, { useCallback, useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { GoogleMap, Marker, InfoWindow, DirectionsRenderer } from '@react-google-maps/api';
import { fetchIsochrone } from '../services/isochroneService';
import { getAddressFromCoordinates } from '../services/geocodingService';
import {
  getSwedishPlaceTypeParameters,
  filterSwedishPlaceResults,
  filterPlacesByActualDistance
} from '../utils/placeSearchTypes';

const containerStyle = {
  width: '100%',
  height: '500px'
};

interface WalkingMapProps {
  center: any;
  onMarkerPlaced: any;
  isPlaceMarkerMode: any;
  onMapLoad: any;
  selectedTypes: any;
  onSearchComplete: any;
}

interface Location {
  lat: number;
  lng: number;
}

const WalkingMap = forwardRef<any, WalkingMapProps>(({
  center,
  onMarkerPlaced,
  isPlaceMarkerMode,
  onMapLoad,
  selectedTypes,
  onSearchComplete
}, ref) => {
  const [map, setMap] = useState<any>(null);
  const [originMarker, setOriginMarker] = useState<Location | null>(null);
  const [markerAddress, setMarkerAddress] = useState<string>('');
  const [isochronePolygon, setIsochronePolygon] = useState<any>(null);
  const [placeMarkers, setPlaceMarkers] = useState<any[]>([]);
  const [selectedPlace, setSelectedPlace] = useState<any>(null);
  const [directions, setDirections] = useState<any>(null);
  const [showIsochrone] = useState<boolean>(true);

  const placesServiceRef = useRef<any>(null);
  const directionsServiceRef = useRef<any>(null);

  const onLoad = useCallback((map: any) => {
    setMap(map);
    placesServiceRef.current = new window.google.maps.places.PlacesService(map);
    directionsServiceRef.current = new window.google.maps.DirectionsService();
    if (onMapLoad) onMapLoad(map, placesServiceRef.current, directionsServiceRef.current);
  }, [onMapLoad]);

  const onUnmount = useCallback(() => {
    setMap(null);
  }, []);

  const handleMapClick = useCallback(async (e: any) => {
    if (isPlaceMarkerMode) {
      const location = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng()
      };

      setOriginMarker(location);

      // Get address
      if (placesServiceRef.current) {
        const address = await getAddressFromCoordinates(e.latLng, placesServiceRef.current);
        setMarkerAddress(address);
      }

      // Fetch isochrone
      try {
        const isoData = await fetchIsochrone(location);
        setIsochronePolygon(isoData);
      } catch (error) {
        console.error('Error fetching isochrone:', error);
      }

      if (onMarkerPlaced) {
        onMarkerPlaced(location);
      }
    }
  }, [isPlaceMarkerMode, onMarkerPlaced]);

  const handleOriginMarkerDrag = useCallback(async (e: any) => {
    const location = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng()
    };

    setOriginMarker(location);

    // Get updated address
    if (placesServiceRef.current) {
      const address = await getAddressFromCoordinates(e.latLng, placesServiceRef.current);
      setMarkerAddress(address);
    }

    // Fetch updated isochrone
    try {
      const isoData = await fetchIsochrone(location);
      setIsochronePolygon(isoData);
    } catch (error) {
      console.error('Error fetching isochrone:', error);
    }

    // Clear routes and places
    setDirections(null);
    setPlaceMarkers([]);

    if (onMarkerPlaced) {
      onMarkerPlaced(location);
    }
  }, [onMarkerPlaced]);

  // Search for places
  const searchPlaces = useCallback(async () => {
    if (!originMarker || !placesServiceRef.current || selectedTypes.length === 0) {
      return;
    }

    const radius = 550;
    const center = originMarker;

    // Categorize place types
    const schoolTypes = selectedTypes.filter((type: string) =>
      ['preschool', 'primary_school', 'secondary_school', 'school'].includes(type));
    const systembolagetType = selectedTypes.includes('systembolaget') ? ['systembolaget'] : [];
    const otherTypes = selectedTypes.filter((type: string) =>
      !schoolTypes.includes(type) && type !== 'systembolaget');

    let allResults: any[] = [];

    // Search Systembolaget
    for (const type of systembolagetType) {
      const results = await searchSystembolaget(center, radius);
      allResults.push(...results);
    }

    // Search school types
    for (const schoolType of schoolTypes) {
      const results = await searchSchoolType(center, radius, schoolType);
      allResults.push(...results);
    }

    // Search other types
    if (otherTypes.length > 0) {
      const results = await searchOtherPlaceTypes(center, radius, otherTypes);
      allResults.push(...results);
    }

    // Remove duplicates
    const uniqueResults = Array.from(
      new Map(allResults.map(place => [place.place_id, place])).values()
    );

    // Process with walking distances
    const processedPlaces = await processPlacesWithDistance(uniqueResults, center);

    // Filter by 10-minute walking time
    const filteredPlaces = processedPlaces.filter(place =>
      place.walkingDurationMinutes && place.walkingDurationMinutes <= 10
    );

    setPlaceMarkers(filteredPlaces);

    if (onSearchComplete) {
      onSearchComplete(filteredPlaces);
    }
  }, [originMarker, selectedTypes, onSearchComplete]);

  // Expose searchPlaces method via ref
  useImperativeHandle(ref, () => ({
    searchPlaces
  }));

  // Expose searchPlaces method via window (for backward compatibility)
  useEffect(() => {
    if (window.walkingMapRef) {
      window.walkingMapRef.searchPlaces = searchPlaces;
    }
  }, [searchPlaces]);

  const searchSystembolaget = (center: any, radius: number): Promise<any[]> => {
    return new Promise((resolve) => {
      const request = {
        location: new window.google.maps.LatLng(center.lat, center.lng),
        radius: radius,
        query: 'Systembolaget',
        type: 'store'
      };

      placesServiceRef.current.textSearch(request, (results: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          const filtered = results.filter((place: any) =>
            place.name === 'Systembolaget' || place.name.startsWith('Systembolaget '));
          resolve(filtered);
        } else {
          resolve([]);
        }
      });
    });
  };

  const searchSchoolType = (center: any, radius: number, schoolType: string): Promise<any[]> => {
    return new Promise((resolve) => {
      const typeParams = getSwedishPlaceTypeParameters(schoolType);
      if (!typeParams) {
        resolve([]);
        return;
      }

      const allResults = new Map();
      let searchesCompleted = 0;
      const totalSearches = 1 + typeParams.keywords.length;

      // Nearby search
      const nearbyRequest = {
        location: new window.google.maps.LatLng(center.lat, center.lng),
        radius: radius,
        type: typeParams.baseType
      };

      placesServiceRef.current.nearbySearch(nearbyRequest, (results: any, status: any) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
          results.forEach((place: any) => {
            if (!allResults.has(place.place_id)) {
              allResults.set(place.place_id, place);
            }
          });
        }
        searchesCompleted++;
        checkCompletion();
      });

      // Text searches
      typeParams.keywords.forEach((keyword: string) => {
        const textRequest = {
          location: new window.google.maps.LatLng(center.lat, center.lng),
          radius: radius,
          query: keyword
        };

        placesServiceRef.current.textSearch(textRequest, (results: any, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            results.forEach((place: any) => {
              if (!allResults.has(place.place_id)) {
                allResults.set(place.place_id, place);
              }
            });
          }
          searchesCompleted++;
          checkCompletion();
        });
      });

      function checkCompletion() {
        if (searchesCompleted === totalSearches) {
          const resultsArray = Array.from(allResults.values());
          const withinRadius = filterPlacesByActualDistance(resultsArray, center, radius);
          const filtered = filterSwedishPlaceResults(withinRadius, schoolType);
          resolve(filtered);
        }
      }
    });
  };

  const searchOtherPlaceTypes = (center: any, radius: number, placeTypes: string[]): Promise<any[]> => {
    return new Promise((resolve) => {
      let allResults: any[] = [];
      let completedSearches = 0;

      placeTypes.forEach((placeType: string) => {
        const request = {
          location: new window.google.maps.LatLng(center.lat, center.lng),
          radius: radius,
          type: placeType
        };

        placesServiceRef.current.nearbySearch(request, (results: any, status: any) => {
          if (status === window.google.maps.places.PlacesServiceStatus.OK && results) {
            allResults.push(...results);
          }
          completedSearches++;

          if (completedSearches === placeTypes.length) {
            resolve(allResults);
          }
        });
      });
    });
  };

  const processPlacesWithDistance = async (places: any[], origin: any): Promise<any[]> => {
    const processedPlaces: any[] = [];

    for (let place of places) {
      try {
        const placeInfo = {
          ...place,
          name: place.name || 'Unknown',
          vicinity: place.vicinity || place.formatted_address || 'No address',
          rating: place.rating || 'No rating',
          types: place.types || []
        };

        if (place.geometry && place.geometry.location) {
          const routeInfo = await calculateWalkingDistance(origin, place.geometry.location);
          placeInfo.walkingDistance = routeInfo.distance;
          placeInfo.walkingDuration = routeInfo.duration;
          placeInfo.walkingDistanceMeters = routeInfo.distanceValue;
          placeInfo.walkingDurationMinutes = routeInfo.durationValue;
        }

        processedPlaces.push(placeInfo);
      } catch (error) {
        console.error(`Error processing place ${place.name}:`, error);
      }
    }

    return processedPlaces;
  };

  const calculateWalkingDistance = (origin: any, destination: any): Promise<any> => {
    return new Promise((resolve) => {
      const request = {
        origin: new window.google.maps.LatLng(origin.lat, origin.lng),
        destination: destination,
        travelMode: window.google.maps.TravelMode.WALKING
      };

      directionsServiceRef.current.route(request, (response: any, status: any) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          const leg = response.routes[0].legs[0];
          resolve({
            distance: leg.distance.text,
            duration: leg.duration.text,
            distanceValue: leg.distance.value,
            durationValue: Math.round(leg.duration.value / 60)
          });
        } else {
          // Fallback to straight-line distance
          const lat1 = origin.lat;
          const lng1 = origin.lng;
          const lat2 = typeof destination.lat === 'function' ? destination.lat() : destination.lat;
          const lng2 = typeof destination.lng === 'function' ? destination.lng() : destination.lng;

          const distance = calculateStraightLineDistance(lat1, lng1, lat2, lng2);
          const duration = Math.round(distance / 83); // 83 m/min walking speed

          resolve({
            distance: `${Math.round(distance)} m`,
            duration: `${duration} min`,
            distanceValue: distance,
            durationValue: duration
          });
        }
      });
    });
  };

  const calculateStraightLineDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371e3;
    const φ1 = lat1 * Math.PI / 180;
    const φ2 = lat2 * Math.PI / 180;
    const Δφ = (lat2 - lat1) * Math.PI / 180;
    const Δλ = (lng2 - lng1) * Math.PI / 180;

    const a = Math.sin(Δφ / 2) * Math.sin(Δφ / 2) +
            Math.cos(φ1) * Math.cos(φ2) *
            Math.sin(Δλ / 2) * Math.sin(Δλ / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return R * c;
  };

  const handlePlaceClick = (place: any) => {
    setSelectedPlace(place);

    // Show route
    if (originMarker && place.geometry && place.geometry.location) {
      const request = {
        origin: new window.google.maps.LatLng(originMarker.lat, originMarker.lng),
        destination: place.geometry.location,
        travelMode: window.google.maps.TravelMode.WALKING
      };

      directionsServiceRef.current.route(request, (response: any, status: any) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(response);
        }
      });
    }
  };

  // Render isochrone polygon
  useEffect(() => {
    if (!map || !isochronePolygon) return;

    const dataLayer = new window.google.maps.Data();
    dataLayer.addGeoJson(isochronePolygon);
    dataLayer.setStyle({
      fillColor: '#4285F4',
      fillOpacity: 0.3,
      strokeColor: '#4285F4',
      strokeWeight: 2,
      strokeOpacity: 0.8,
      clickable: false
    });

    if (showIsochrone) {
      dataLayer.setMap(map);
    }

    return () => {
      dataLayer.setMap(null);
    };
  }, [map, isochronePolygon, showIsochrone]);

  return (
    <GoogleMap
      mapContainerStyle={containerStyle}
      center={center}
      zoom={15}
      onLoad={onLoad}
      onUnmount={onUnmount}
      onClick={handleMapClick}
      options={{
        mapTypeControl: true,
        fullscreenControl: true,
        scrollwheel: true
      }}
    >
      {originMarker && (
        <Marker
          position={originMarker}
          draggable={true}
          onDragEnd={handleOriginMarkerDrag}
          icon={{
            url: "https://maps.google.com/mapfiles/ms/icons/blue-dot.png"
          }}
        />
      )}

      {placeMarkers.map((place, index) => {
        const position = place.geometry && place.geometry.location
          ? {
              lat: typeof place.geometry.location.lat === 'function'
                ? place.geometry.location.lat()
                : place.geometry.location.lat,
              lng: typeof place.geometry.location.lng === 'function'
                ? place.geometry.location.lng()
                : place.geometry.location.lng
            }
          : null;

        if (!position) return null;

        return (
          <Marker
            key={`${place.place_id}-${index}`}
            position={position}
            onClick={() => handlePlaceClick(place)}
            icon={{
              url: 'https://maps.google.com/mapfiles/ms/icons/green-dot.png'
            }}
          />
        );
      })}

      {selectedPlace && selectedPlace.geometry && selectedPlace.geometry.location && (
        <InfoWindow
          position={{
            lat: typeof selectedPlace.geometry.location.lat === 'function'
              ? selectedPlace.geometry.location.lat()
              : selectedPlace.geometry.location.lat,
            lng: typeof selectedPlace.geometry.location.lng === 'function'
              ? selectedPlace.geometry.location.lng()
              : selectedPlace.geometry.location.lng
          }}
          onCloseClick={() => setSelectedPlace(null)}
        >
          <div>
            <h3>{selectedPlace.name}</h3>
            <p>{selectedPlace.vicinity}</p>
            <p>Walking Distance: {selectedPlace.walkingDistance}</p>
            <p>Walking Time: {selectedPlace.walkingDuration}</p>
            {selectedPlace.rating && <p>Rating: {selectedPlace.rating} ⭐</p>}
          </div>
        </InfoWindow>
      )}

      {directions && (
        <DirectionsRenderer
          directions={directions}
          options={{
            suppressMarkers: true,
            preserveViewport: true
          }}
        />
      )}
    </GoogleMap>
  );
});

export default WalkingMap;
